import { Injectable, OnDestroy, effect, signal, untracked } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { MixpanelAnalyticsProvider } from '@shure/cloud/shared/utils/analytics';
import { AnalyticsService } from '@shure/shared/angular/utils/analytics';
import { PageViewedEvent } from '@shure/shared/models';

/**
 * Whitelist of page paths to track.
 * Routes must match exactly after normalization.
 */
export const pagesToTrack: string[] = ['/devices', '/pending-devices', '/claim', '/scheduled'];

/**
 * Service that tracks page views by listening to router navigation events.
 * Automatically sends analytics events when users navigate to different pages.
 */
@Injectable({
	providedIn: 'root'
})
export class PageTrackingService implements OnDestroy {
	private destroy$ = new Subject<void>();
	private currentPage = signal<string>('');

	constructor(
		private readonly router: Router,
		private readonly analyticsProvider: MixpanelAnalyticsProvider,
		private readonly analyticsService: AnalyticsService
	) {
		this.initialize();

		effect(() => {
			const ready = this.analyticsProvider.isMixpanelInitialized();
			if (ready && this.currentPage()) {
				// untracked to avoid sending a false page viewed event if user opts out and then back in to Mixpanel
				untracked(() => this.trackPageView(this.currentPage()));
			}
		});
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private initialize(): void {
		// Subscribe to router navigation events for subsequent navigations
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				takeUntil(this.destroy$)
			)
			.subscribe((navigationEnd) => {
				const normalizedPath = this.normalizePath(navigationEnd.urlAfterRedirects);
				const shouldTrack = pagesToTrack.includes(normalizedPath);

				if (shouldTrack) {
					this.currentPage.set(normalizedPath);
				}
			});
	}

	private normalizePath(url: string): string {
		const path = url.split('?')[0];

		if (path.startsWith('/claim')) {
			return '/claim';
		}

		return path;
	}

	private trackPageView(url: string): void {
		this.analyticsService.trackEvent(new PageViewedEvent(url));
	}
}
